(ns common.mui
  (:require ["@mui/material/CssBaseline$default" :as CssBaseline]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/material/styles" :as mui-styles]
            ["@mui/x-license" :refer [LicenseInfo]]
            ["react-use/lib/useEvent$default" :as useEvent]
            ["src/svg/chevron/chevron-down.svg$default" :as ChevronDown]
            ["src/svg/objects/calendar.svg$default" :as Calendar]
            ["src/svg/shapes/other/traingle-fill-down.svg$default" :as TriangleDown]
            ["src/svg/shapes/other/triangle-fill-right.svg$default" :as TriangleRight]
            [clojure.string :as str]
            [common.os :as os]
            [common.util :refer [format-iso-string]]
            [goog.object :as gobject]
            [medley.core :refer [deep-merge]]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]])
  (:require-macros [common.mui :refer [emit-theme-vars]]
                   ;; Include extra UIx linters to check MUI elements
                   [dev.linters]))

(defn set-license []
  (.setLicenseKey LicenseInfo "d05d493a9ee1554ed3684a4dbefdf00cTz0xMjAwNjUsRT0xNzkzNTc3NjAwMDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9UTMtMjAyNCxLVj0y"))

;; From platform-ui-kit/packages/components-library/.storybook/preview-head.html
;; Font files are loaded only once an element uses the font,
;; so it could be reasonable to concat all this css to one file and load
;; it always, so we don't need to select css files based on the theme.
(def fonts
  {"Inter" "https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap"
   "Roboto" "https://fonts.googleapis.com/css2?family=Roboto:wght@100;200;300;400;500;600;700;800;900&display=swap"
   "Mulish" "https://public-assets.os.wpp.com/fonts/mulish/index.css"
   "Poppins" "https://public-assets.os.wpp.com/fonts/poppins/index.css"
   "Ogilvy Sans" "https://public-assets.os.wpp.com/fonts/ogilvy-sans/index.css"
   "WTGothic" "https://public-assets.os.wpp.com/fonts/wt-gothic/index.css"
   "Snowflake Sans" "https://public-assets.os.wpp.com/fonts/snowflake-sans/index.css"
   "Open Sans" "https://public-assets.os.wpp.com/fonts/open-sans/index.css"
   "Unity" "https://public-assets.os.wpp.com/fonts/unity/index.css"
   "Outfit" "https://public-assets.os.wpp.com/fonts/outfit/index.css"
   "halyard-display" "https://use.typekit.net/gsk0gei.css"
   "aktiv-grotesk" "https://use.typekit.net/dav7aoj.css"})

(def breakpoints
  {:values {:xs 0
            :sm 600
            :md 900
            :lg 1200
            :xl 1536}})

(defn switch-theme-overrides [^js cfg]
  (let [theme (.. cfg -theme)
        primary-color (.. theme -palette -primary -main)
        background-color (gobject/get (.. theme -palette -grey) 50)]
    (clj->js
     {:width 38
      :height 24
      :padding 0
      :margin 0
      ".MuiSwitch-switchBase" {:display "block"
                               :position "relative"
                               :width "100%"
                               :height "100%"
                               :padding 0
                               :margin 0
                               :borderRadius 64
                               "& .MuiSwitch-input" {:width "100%"
                                                     :height "100%"
                                                     :position "relative"
                                                     :left 0
                                                     :top 0}
                               "&.Mui-checked" {:transform "translate(0)"}
                               "&.Mui-checked + .MuiSwitch-track" {:backgroundColor primary-color
                                                                   :opacity 1}
                               "&.Mui-disabled + .MuiSwitch-track" {:cursor "not-allowed"
                                                                    :opacity 0.3}
                               "&.Mui-disabled .MuiSwitch-thumb" {:opacity 0.3
                                                                  :background background-color}}
      ".MuiSwitch-track" {:position "absolute"
                          :border 0
                          :borderRadius 64
                          :backgroundColor background-color
                          :opacity 1}
      ".MuiSwitch-thumb" {:position "absolute"
                          :width 20
                          :height 20
                          :backgroundColor "#ffffff"
                          :opacity 1
                          :top "50%"
                          :left "50%"
                          :transform "translateX(-17px) translateY(-50%)"
                          :transitionDuration "250ms"}
      "& .Mui-checked .MuiSwitch-thumb" {:transform "translateX(-3px) translateY(-50%)"}})))

(def SelectIcon
  (uix/as-react
   (fn [props]
     ($ SvgIcon props
        ($ ChevronDown)))))

(def select-option-overriders
  {:height "40px"
   :padding "8px 16px"
   :gap "8px"
   :borderRadius "6px"
   "& .MuiListItemIcon-root" {:minWidth "unset"}
   "& .MuiCheckbox-root" {:padding 0}})

(def CheckboxIcon
  (uix/as-react
   (fn [_props]
     ($ ((mui-styles/styled "span")
         (fn [^js cfg]
           #js {:borderRadius 4
                :width 20
                :height 20
                :borderWidth "1px"
                :borderStyle "solid"
                :borderColor (gobject/get (.. cfg -theme -palette -grey) "50")
                "input:hover ~ &" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "80")}
                "input:active ~ &" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "70")}
                "input:disabled ~ &" #js {:borderColor (gobject/get (.. cfg -theme -palette -grey) "60")}}))))))

(def CheckboxCheckedIcon
  (uix/as-react
   (fn [_props]
     ($ ((mui-styles/styled "span")
         (fn [^js cfg]
           #js {:borderRadius 4
                :width 20
                :height 20
                :backgroundColor (.. cfg -theme -palette -primary -main)
                "&::before" #js {:display "block"
                                 :width 16
                                 :height 16
                                 :backgroundImage
                                 "url(\"data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M14.9795%206.81606C15.3573%207.21897%2015.3369%207.85181%2014.9339%208.22954L9.94258%2012.9089C9.36559%2013.4499%208.46775%2013.4499%207.89076%2012.9089L5.56606%2010.7295C5.16315%2010.3518%205.14274%209.71897%205.52047%209.31606C5.8982%208.91315%206.53103%208.89274%206.93394%209.27047L8.91667%2011.1293L13.5661%206.77047C13.969%206.39274%2014.6018%206.41315%2014.9795%206.81606Z%22%20fill%3D%22white%22/%3E%3C/svg%3E\")"
                                 :content "\"\""}
                "input:hover ~ &" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -primary) "400")}
                "input:active ~ &" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -primary) "600")}
                "input:disabled ~ &" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -primary) "300")}}))))))

(def CheckboxIndeterminateIcon
  (uix/as-react
   (fn [_props]
     ($ ((mui-styles/styled "span")
         (fn [^js cfg]
           (letfn [(svg-data-uri [color]
                     (let [svg-str (str
                                    "<svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>"
                                    "<path fill-rule='evenodd' clip-rule='evenodd' d='M6 10C6 9.44772 6.44772 9 7 9H13C13.5523 9 14 9.44772 14 10C14 10.5523 13.5523 11 13 11H7C6.44772 11 6 10.5523 6 10Z' fill='" color "'/>"
                                    "</svg>")
                           encoded (js/encodeURIComponent svg-str)]
                       (str "url(\"data:image/svg+xml;charset=utf-8," encoded "\")")))]
             #js {:borderRadius 4
                  :width 20
                  :height 20
                  :borderWidth "1px"
                  :borderStyle "solid"
                  :borderColor (.. cfg -theme -palette -primary -main)
                  :backgroundColor (gobject/get (.. cfg -theme -palette -primary) "100")
                  "&::before" #js {:display "block"
                                   :width 16
                                   :height 16
                                   :margin "-1px 0 0 -1px"
                                   :backgroundImage (svg-data-uri (.. cfg -theme -palette -primary -main))
                                   :content "\"\""}
                  "input:hover ~ &" #js {:borderColor (gobject/get (.. cfg -theme -palette -primary) "400")
                                         "&::before" #js {:backgroundImage (svg-data-uri (gobject/get (.. cfg -theme -palette -primary) "400"))}}
                  "input:active ~ &" #js {:borderColor (gobject/get (.. cfg -theme -palette -primary) "600")
                                          :backgroundColor (gobject/get (.. cfg -theme -palette -primary) "200")
                                          "&::before" #js {:backgroundImage (svg-data-uri (gobject/get (.. cfg -theme -palette -primary) "600"))}}
                  "input:disabled ~ &" #js {:borderColor (gobject/get (.. cfg -theme -palette -primary) "300")
                                            "&::before" #js {:backgroundImage (svg-data-uri (gobject/get (.. cfg -theme -palette -primary) "300"))}}})))))))

(def CalendarHeader
  (uix/as-react
   (fn [{:keys [currentMonth onMonthChange]}]
     (let [select-next-month #(onMonthChange (-> ^js currentMonth
                                                 (.clone)
                                                 (.add 1 "month"))
                                             "left")
           select-previous-month #(onMonthChange (-> ^js currentMonth
                                                     (.clone)
                                                     (.subtract 1 "month"))
                                                 "right")]
       ($ Stack
          {:direction "row"
           :justifyContent "space-between"
           :alignItems "center"
           :sx #js {:pb 1
                    :borderBottom "1px solid"
                    :borderColor "grey.70"}}
          ($ IconButton
             {:onClick select-previous-month}
             ($ Icon "keyboard_arrow_left"))
          ($ Typography {:fontWeight "600"}
             (format-iso-string currentMonth "MMMM YYYY"))
          ($ IconButton
             {:onClick select-next-month}
             ($ Icon "keyboard_arrow_right")))))))

(def components
  {:MuiAccordion {:defaultProps {:elevation 0}
                  :styleOverrides {:root {:backgroundColor "unset"
                                          "&::before" #js {:display "none"}
                                          "&.Mui-expanded" {:margin 0}}}}
   :MuiAccordionSummary {:defaultProps {:expandIcon ($ Icon "expand_more")}
                         :styleOverrides {:root {:flexDirection "row-reverse"
                                                 :gap "8px"
                                                 :padding "4px 0px"
                                                 :minHeight 0
                                                 "&.Mui-expanded" {:minHeight 0}}
                                          :expandIconWrapper {:transform "rotate(-90deg)"
                                                              "&.Mui-expanded" {:transform "rotate(0deg)"}}
                                          :content {:gap "8px"
                                                    :margin 0
                                                    "&.Mui-expanded" {:margin 0}}}}
   :MuiAccordionDetails {:styleOverrides {:root {:padding "8px 4px"}}}
   :MuiAlert {:styleOverrides {:root (fn [^js cfg]
                                       #js {:fontSize "inherit"
                                            :color (.. cfg -theme -palette -text -primary)})
                               :standardSuccess (fn [^js cfg]
                                                  #js {:backgroundColor (.. cfg -theme -palette -success -background)})
                               :standardWarning (fn [^js cfg]
                                                  #js {:backgroundColor (.. cfg -theme -palette -warning -background)})
                               :standardError (fn [^js cfg]
                                                #js {:backgroundColor (.. cfg -theme -palette -error -background)})
                               :standardInfo (fn [^js cfg]
                                               #js {:backgroundColor (.. cfg -theme -palette -info -background)})
                               :standardDefault (fn [^js cfg]
                                                  #js {:color "text.primary"
                                                       :backgroundColor (gobject/get (.. cfg -theme -palette -grey) "70")
                                                       ".MuiAlert-icon" #js {:color (gobject/get (.. cfg -theme -palette -grey) "30")}})}
              :variants [{:props {:variant "inline"}
                          :style (fn [^js cfg]
                                   #js {:padding "16px"
                                        :color "grey.100"
                                        :backgroundColor (gobject/get (.. cfg -theme -palette -grey) "70")
                                        ".MuiAlert-icon" #js {:alignSelf "center"
                                                              :marginRight "4px"}
                                        ".MuiAlert-message" #js {:padding 0
                                                                 :lineHeight "22px"}})}]}
   ;; Inconsistency in MUI, MuiAutcomplete :popupIcon expects type `node`,
   ;; whilst MuiSelect :IconComponent expects type `elementType`.
   :MuiAutocomplete {:defaultProps {:popupIcon ($ SelectIcon)
                                    :slotProps {:paper {:elevation 2}}}
                     :styleOverrides {:option select-option-overriders}}
   :MuiSelect {:defaultProps {:IconComponent SelectIcon}}
   :MuiMenu {:defaultProps {:elevation 2}}
   :MuiPaper {:styleOverrides {:root {:borderRadius "8px"
                                      ".MuiPopper-root &, .MuiPopover-root &" {:borderRadius "6px"}}
                               :elevation0 (fn [^js cfg]
                                             #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "90")})}}
   :MuiMenuItem {:styleOverrides {:root select-option-overriders}}
   :MuiButton {:styleOverrides {:root {:textTransform "none"
                                       :padding "4px 8px"
                                       "& .MuiIcon-root, & .MuiSvgIcon-root" {:color "inherit"}}
                                :sizeSmall {:fontSize "1.2rem"}
                                ;; https://developers.os.wpp.com/docs/design-system/components/actions/link#contextual
                                :textSecondary (fn [^js cfg]
                                                 #js {:color (gobject/get (.. cfg -theme -palette -text) "primary")
                                                      :textDecoration "underline"
                                                      "&:hover" #js {:color (gobject/get (.. cfg -theme -palette -primary) "main")
                                                                     :textDecoration "underline"
                                                                     :backgroundColor "transparent"}})}
               :variants [{;; Custom contained grey color variant
                           :props {:variant "contained"
                                   :color "grey"}
                           :style (fn [^js cfg]
                                    #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "70")
                                         :color (.. cfg -theme -palette -text -subtle)
                                         :boxShadow "none"
                                         "&.Mui-disabled" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "80")}
                                         "&:hover" #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "60")
                                                        :boxShadow "none"}})}]}
   :MuiLink {:styleOverrides {:button {;; https://developers.os.wpp.com/docs/design-system/components/actions/link#primary
                                       :fontWeight 400}}}
   :MuiOutlinedInput {:defaultProps {:fullWidth true
                                     :size "small"}}
   :MuiFilledInput {:styleOverrides {:root (fn [^js cfg]
                                             #js {:borderRadius (.. cfg -theme -shape -borderRadius)
                                                  ".MuiFilledInput-input" #js {:paddingTop "6px"
                                                                               :paddingBottom "6px"
                                                                               ":focus" #js {:borderRadius "inherit"}}
                                                  "::before, ::after" #js {:content "unset"}})}}
   :MuiCard {:styleOverrides {:root {:display "flex"
                                     :flexDirection "column"
                                     ".MuiCardContent-root" {:flex 1}}}}
   :MuiChip {:defaultProps {:size "small"
                            :color "default"}
             :styleOverrides {:root {:borderRadius "4px"}
                              :colorDefault (fn [^js cfg]
                                              #js {:backgroundColor (mui-styles/alpha
                                                                     (gobject/get (.. cfg -theme -palette -data -grey) "300")
                                                                     0.25)})}
             ;; Custom variant with light bg colors for e.g. status chip
             :variants [{:props {:variant "subtle"}
                         :style #js {}}
                        {:props {:variant "subtle"
                                 :color "primary"}
                         :style (fn [^js cfg]
                                  #js {:color (gobject/get (.. cfg -theme -palette -primary) "main")
                                       :backgroundColor (gobject/get (.. cfg -theme -palette -primary) "200")})}
                        {:props {:variant "subtle"
                                 :color "success"}
                         :style (fn [^js cfg]
                                  #js {:color (gobject/get (.. cfg -theme -palette -success) "dark")
                                       :backgroundColor (gobject/get (.. cfg -theme -palette -success) "background")})}
                        {:props {:variant "subtle"
                                 :color "error"}
                         :style (fn [^js cfg]
                                  #js {:color (gobject/get (.. cfg -theme -palette -error) "dark")
                                       :backgroundColor (gobject/get (.. cfg -theme -palette -error) "background")})}
                        {:props {:variant "subtle"
                                 :color "warning"}
                         :style (fn [^js cfg]
                                  #js {:color (gobject/get (.. cfg -theme -palette -warning) "dark")
                                       :backgroundColor (gobject/get (.. cfg -theme -palette -warning) "background")})}]}
   :MuiCssBaseline {:styleOverrides {:html {:fontSize "62.5%"}
                                     :body {"& *" {"&::-webkit-scrollbar" #js {:width "12px"
                                                                               :height "12px"}
                                                   "&::-webkit-scrollbar-track" #js {:opacity 0}
                                                   "&::-webkit-scrollbar-thumb" #js {:backgroundColor "grey"
                                                                                     :borderRadius "10px"
                                                                                     :border "2px solid transparent"
                                                                                     :backgroundClip "content-box"}
                                                   "&::-webkit-scrollbar-corner" #js {:backgroundColor "transparent"}}}
                                     :p {:margin 0}}}
   :MuiToggleButton {:styleOverrides {:root (fn [^js cfg]
                                              #js {:textTransform "none"
                                                   :whiteSpace "nowrap"
                                                   :padding "10px"
                                                   "&.Mui-selected" #js {:color (.. cfg -theme -palette -primary -main)
                                                                         :backgroundColor (gobject/get (.. cfg -theme -palette -primary) "100")
                                                                         :borderColor (str (.. cfg -theme -palette -primary -main) "!important")}
                                                   "&.Mui-disabled.Mui-selected" #js {:borderLeftColor (.. cfg -theme -palette -primary -main)}
                                                   "& .MuiIcon-root, & .MuiSvgIcon-root" #js {:color "inherit"}})
                                      :sizeSmall {:fontSize "inherit"}}
                     :variants [{:props {:size "small"}
                                 :style #js {:padding "4px 8px"}}]}
   :MuiRadio {:styleOverrides {:root {".MuiSvgIcon-root" {:color "inherit"}}}}
   :MuiStepper {:styleOverrides {:root (fn [^js cfg]
                                         #js {".MuiStepConnector-root" #js {:zIndex -1
                                                                            :top 10
                                                                            :left "calc(-50% + 10px)"
                                                                            :right "calc(50% + 10px)"}
                                              ".MuiStepConnector-line" #js {:borderWidth "2px"}
                                              ".MuiStep-root:has(.Mui-completed, .Mui-active) .MuiStepConnector-line" #js {:borderColor (.. cfg -theme -palette -primary -main)}

                                              ".MuiStepIcon-root" #js {:overflow "visible"}
                                              ".MuiStepIcon-root circle" #js {:fill "none"
                                                                              :stroke (gobject/get (.. cfg -theme -palette -grey) "60")
                                                                              :stroke-width "2px"}
                                              ".MuiStepIcon-root text" #js {:fill (gobject/get (.. cfg -theme -palette -grey) "60")}
                                              ".MuiStepIcon-root.Mui-active circle" #js {:fill (gobject/get (.. cfg -theme -palette -primary) "100")
                                                                                         :stroke (.. cfg -theme -palette -primary -main)}
                                              ".MuiStepIcon-root.Mui-active text" #js {:stroke (.. cfg -theme -palette -primary -main)}

                                              ".MuiStep-root" #js {:padding 0}
                                              ".MuiStepLabel-label" #js {:padding "0 12px"
                                                                         :maxWidth "max-content"
                                                                         :marginLeft "auto"
                                                                         :marginRight "auto"
                                                                         :color (gobject/get (.. cfg -theme -palette -grey) "50")}
                                              ".MuiStepLabel-label.Mui-active" #js {:borderRadius "6px"
                                                                                    :color (.. cfg -theme -palette -primary -main)
                                                                                    :backgroundColor (gobject/get (.. cfg -theme -palette -primary) "100")}
                                              ".MuiStepLabel-label.Mui-completed" #js {:color (.. cfg -theme -palette -text -primary)}})}}
   :MuiIconButton {:defaultProps {:color "grey"}
                   :styleOverrides {:root {:borderRadius 6
                                           "& .MuiIcon-root, & .MuiSvgIcon-root" {:color "inherit"}}}}
   :MuiIcon {:defaultProps {:color "default"}
             :styleOverrides {:root (fn [^js cfg]
                                      ;; Figma uses 24px, but this is probably 20px.
                                      #js {:fontSize (.. cfg -theme -typography -xl)})
                              :colorDefault (fn [^js cfg]
                                              #js {:color (.. cfg -theme -palette -text -subtle)})
                              :fontSizeSmall (fn [^js cfg]
                                               #js {:fontSize (.. cfg -theme -typography -md)})
                              :fontSizeLarge (fn [^js cfg]
                                               #js {:fontSize (.. cfg -theme -typography -xxl)})
                              :fontSizeExtraLarge {:fontSize "40px"}}}
   :MuiSvgIcon {;; Make SVG icons default to Figma color.
                :defaultProps {:color "default"}
                :variants [{:props {:color "grey"}
                            :style (fn [^js cfg]
                                     #js {:fill (gobject/get (.. cfg -theme -palette -grey) "40")})}]
                :styleOverrides {:root (fn [^js cfg]
                                         #js {:fontSize (.. cfg -theme -typography -xl)})
                                 :colorDefault (fn [^js cfg]
                                                 #js {:color (.. cfg -theme -palette -text -subtle)})
                                 :fontSizeSmall (fn [^js cfg]
                                                  #js {:fontSize (.. cfg -theme -typography -lg)})
                                 :fontSizeLarge (fn [^js cfg]
                                                  #js {:fontSize (.. cfg -theme -typography -xxl)})}}
   :MuiTooltip {:defaultProps {:arrow true}
                :styleOverrides {:tooltip (fn [^js cfg]
                                            #js {:fontSize "inherit"
                                                 :fontWeight 400
                                                 :boxShadow (get (.. cfg -theme -shadows) 2)


                                                 :variants #js [#js {:props #js {:color "error"}
                                                                     :style #js {:color (.. cfg -theme -palette -text -primary)
                                                                                 :backgroundColor (.. cfg -theme -palette -error -background)
                                                                                 ".MuiTooltip-arrow" #js {:color (.. cfg -theme -palette -error -background)}}}
                                                                #js {:props #js {:color "warning"}
                                                                     :style #js {:color (.. cfg -theme -palette -text -primary)
                                                                                 :backgroundColor (.. cfg -theme -palette -warning -background)
                                                                                 ".MuiTooltip-arrow" #js {:color (.. cfg -theme -palette -warning -background)}}}]})}}
   :MuiDataGrid {:defaultProps {:columnHeaderHeight 50
                                :disableAggregation true
                                :disableRowGrouping true
                                :slotProps {:pagination {:nextIconButtonProps {:data-testid "next-page"}
                                                         :backIconButtonProps {:data-testid "previous-page"}}}}
                 :styleOverrides {:root (fn [^js cfg]
                                          (let [^js palette (.. cfg -theme -palette)]
                                            #js {:border 0
                                                 :fontSize "inherit"
                                                 :borderLeft "0"
                                                 :borderRight "0"
                                                 :borderRadius "0"

                                                 ;; Workaround to get resizing datagrids smaller working:
                                                 ;; https://github.com/mui/mui-x/issues/8463
                                                 :overflow "hidden"

                                                 ".MuiDataGrid-columnHeader, .MuiDataGrid-cell" #js {:padding "0 16px"}
                                                 ".MuiDataGrid-columnHeaderCheckbox, .MuiDataGrid-cellCheckbox" #js {:padding "0"}

                                                 ".MuiDataGrid-cell" #js {:display "flex"
                                                                          :alignItems "center"}

                                                 ".MuiDataGrid-columnHeaderTitle" #js {:color (gobject/get (.. cfg -theme -palette -grey) "20")}

                                                 ".MuiDataGrid-columnHeader:focus-within" #js {:outline "unset"}
                                                 ".MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--sorted"
                                                 #js {:color (.. palette -primary -main)
                                                      :borderBottomWidth "3px"
                                                      :borderBottomStyle "solid"
                                                      :borderBottomColor (.. palette -primary -main)}

                                                 ".MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon"
                                                 #js {:color (.. palette -primary -main)}}))}}
   :MuiCheckbox {:defaultProps {:disableRipple true
                                :icon ($ CheckboxIcon)
                                :checkedIcon ($ CheckboxCheckedIcon)
                                :indeterminateIcon ($ CheckboxIndeterminateIcon)}}
   :MuiRichTreeView {:defaultProps {:slots {:expandIcon TriangleRight
                                            :collapseIcon TriangleDown}}
                     :styleOverrides {:root (fn [^js cfg]
                                              #js {".MuiTreeItem-iconContainer svg path"
                                                   #js {:fill (gobject/get (.. cfg -theme -palette -grey) "40")}})}}
   :MuiTreeItem {:styleOverrides {:root #js {:marginTop "4px"}}}
   :MuiSwitch {:styleOverrides {:root switch-theme-overrides}}
   :MuiPickersLayout {:styleOverrides {:root (fn [^js cfg]
                                               #js {:padding "8px"

                                                    ".MuiPickersLayout-actionBar"
                                                    #js {:borderTop "1px solid"
                                                         :borderColor (gobject/get (.. cfg -theme -palette -grey) "70")}

                                                    ".MuiPickersLayout-shortcuts"
                                                    #js {:borderRight "1px solid"
                                                         :borderColor (gobject/get (.. cfg -theme -palette -grey) "70")
                                                         :paddingRight "8px"}})}}
   :MuiDatePicker {:defaultProps {:slots {:calendarHeader CalendarHeader
                                          :openPickerIcon Calendar}
                                  :slotProps {:desktopPaper {:elevation 2}
                                              :inputAdornment {:position "start"}}}}
   :MuiTimePicker {:defaultProps {:slotProps {:actionBar {:actions []}
                                              :desktopPaper {:elevation 2}
                                              :inputAdornment {:position "start"}}
                                  :ampm false}}
   :MuiDateTimePicker {:defaultProps {:slots {:calendarHeader CalendarHeader
                                              :openPickerIcon Calendar}
                                      :slotProps {:actionBar {:actions []}
                                                  :desktopPaper {:elevation 2}
                                                  :inputAdornment {:position "start"}}
                                      :ampm false}}
   :MuiDateRangePicker {:defaultProps {:calendars 1
                                       :slots {:calendarHeader CalendarHeader}
                                       :slotProps {:actionBar {:actions ["clear" "accept"]}
                                                   :desktopPaper {:elevation 2}}}}
   :MuiPickersDay {:styleOverrides {:root (fn [^js cfg]
                                            #js {:borderRadius "4px"

                                                 "&.MuiPickersDay-today"
                                                 #js {:borderColor "transparent"
                                                      :fontWeight 600}
                                                 "&.MuiPickersDay-today:not(.Mui-selected)"
                                                 #js {:color (.. cfg -theme -palette -primary -main)}})}}
   :MuiDateRangePickerDay {:styleOverrides {:root (fn [^js cfg]
                                                    #js {:borderRadius 0
                                                         ;; Adjust color of not selected hovered day
                                                         ":not(.MuiDateRangePickerDay-rangeIntervalDayHighlight) .MuiDateRangePickerDay-day:hover:not(.Mui-selected)"
                                                         #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "70")
                                                              :borderColor "transparent"}

                                                         ;; Remove border-radius from first day of each row
                                                         "&:first-of-type, &:first-of-type .MuiDateRangePickerDay-rangeIntervalDayPreview"
                                                         #js {:borderTopLeftRadius "0px"
                                                              :borderBottomLeftRadius "0px"}

                                                         ;; Remove border-radius from last day of each row
                                                         "&:last-of-type, &:last-of-type .MuiDateRangePickerDay-rangeIntervalDayPreview"
                                                         #js {:borderTopRightRadius "0px"
                                                              :borderBottomRightRadius "0px"}

                                                         ;; Give border-radius to left corners of the start of selected/previewed range
                                                         "&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart, .MuiDateRangePickerDay-rangeIntervalDayPreviewStart"
                                                         #js {".MuiDateRangePickerDay-day" #js {:borderRadius "4px 0 0 4px"}}

                                                         ;; Give border-radius to right corners of the end of selected/previewed range
                                                         "&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd, .MuiDateRangePickerDay-rangeIntervalDayPreviewEnd"
                                                         #js {".MuiDateRangePickerDay-day" #js {:borderRadius "0 4px 4px 0"}}

                                                         ;; Reset border-radius for days that are both the start and end of a range
                                                         "&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd, .MuiDateRangePickerDay-rangeIntervalDayPreviewStart.MuiDateRangePickerDay-rangeIntervalDayPreviewEnd"
                                                         #js{".MuiDateRangePickerDay-day" #js {:borderRadius "4px"}}

                                                         ;; Selected range color
                                                         "&.MuiDateRangePickerDay-rangeIntervalDayHighlight"
                                                         #js {:backgroundColor (gobject/get (.. cfg -theme -palette -primary) "100")}

                                                         ;; Range preview color
                                                         ".MuiDateRangePickerDay-rangeIntervalDayPreview"
                                                         #js {:backgroundColor (gobject/get (.. cfg -theme -palette -grey) "90")
                                                              :borderColor "transparent !important"
                                                              :borderRadius 0}})}}
   :MuiMultiSectionDigitalClock {:styleOverrides {:root (fn [^js cfg]
                                                          #js {:borderBottom "0"

                                                               ;; Add padding when adjacent to datepicker
                                                               ".MuiDivider-root + &"
                                                               #js {:paddingLeft "8px"}

                                                               ;; Fix padding and width
                                                               ".MuiMultiSectionDigitalClockSection-root"
                                                               #js {:paddingRight "8px"
                                                                    :paddingLeft "8px"
                                                                    :width "88px"}
                                                               ".MuiMultiSectionDigitalClockSection-root:first-of-type"
                                                               #js {:paddingRight "8px"
                                                                    :paddingLeft 0}
                                                               ".MuiMultiSectionDigitalClockSection-root:last-of-type"
                                                               #js {:paddingRight 0
                                                                    :paddingLeft "8px"}

                                                               ;; Style items
                                                               ".MuiMultiSectionDigitalClockSection-item"
                                                               #js {:padding "4px 8px"
                                                                    :borderRadius "6px"
                                                                    :height "unset"
                                                                    :width "100%"
                                                                    :margin "0 0 4px 0"
                                                                    :justifyContent "start"}
                                                               ".MuiMultiSectionDigitalClockSection-item.Mui-selected"
                                                               #js {:backgroundColor (gobject/get (.. cfg -theme -palette -primary) "100")
                                                                    :color (.. cfg -theme -palette -primary -main)}})}}
   :MuiBackdrop {:styleOverrides {:root (fn [^js cfg]
                                          #js {:backgroundColor (.. cfg -theme -palette -overlay -background)
                                               "&.MuiBackdrop-invisible" #js {:backgroundColor "transparent"}})}}
   :MuiDialog {:defaultProps {:PaperProps {:elevation 3}}
               :styleOverrides {:paper {;; Remove dialog background gradient, just use color
                                        ;; To match assemble upload modal design
                                        :backgroundImage "none"
                                        :borderRadius "12px"}}}
   :MuiPagination {:defaultProps {:shape "rounded"}}
   :MuiTab {:styleOverrides {:root {:textTransform "none"}}}
   :MuiFormHelperText {:styleOverrides {:root {:marginLeft 0}}}
   :SlateEditable {:styleOverrides {:root (fn [^js cfg]
                                            (let [^js palette (.. cfg -theme -palette)]
                                              #js {:borderRadius (.. cfg -theme -shape -borderRadius)
                                                   :padding "5px 12px 5px 11px"
                                                   :borderWidth "1px"
                                                   :borderStyle "solid"
                                                   :borderColor (if (= (.. palette -mode) "light")
                                                                  "rgba(0,0,0,0.23)"
                                                                  "rgba(255,255,255,0.23)")
                                                   "&:hover" #js {:borderColor (if (= (.. palette -mode) "light")
                                                                                 "rgb(0,0,0)"
                                                                                 "rgb(255,255,255)")}
                                                   "&:focus" #js {:outline "none"
                                                                  :padding "4px 11px 4px 10px"
                                                                  :borderWidth "2px"
                                                                  :borderColor (.. palette -primary -main)}}))
                                    :error (fn [^js cfg]
                                             (let [^js palette (.. cfg -theme -palette)
                                                   errorColor (.. palette -error -main)]
                                               #js {:borderColor errorColor
                                                    "&:focus" #js {:borderColor errorColor}}))}}
   :SlateEditableReadOnly {:styleOverrides {:root {:color "grey.20"
                                                   :mt 1
                                                   :whiteSpace "pre-wrap"
                                                   :lineHeight "1.5"}}}})

(defn calc-font-size [expected-font-size]
  (* (/ 14 16) expected-font-size))

(def typography
  (let [;; Default values from create-pro.json, not sure if these make sense with OS themes
        default-font-family "-apple-system,BlinkMacSystemFont,\"Segoe UI\",Roboto,\"Helvetica Neue\",Arial,sans-serif,\"Apple Color Emoji\",\"Segoe UI Emoji\",\"Segoe UI Symbol\""
        monospace-font-family "ui-monospace, SFMono-Regular, Menlo, Consolas, \"Liberation Mono\", monospace"
        font-size {:html 10
                   :base 14
                   :sm "1.2rem"
                   :md "1.4rem"
                   :lg "1.6rem"
                   :xl "2rem"
                   :xxl "2.4rem"}
        line-height {:sm "1.6rem"
                     :md "2rem"
                     :lg "2.4rem"
                     :xl "3.2rem"}]
    {:fontFamily default-font-family
     :fontFamilyMonospace monospace-font-family
     :htmlFontSize (:html font-size)
     :fontSize (calc-font-size (:base font-size))
     :h1 {:fontSize (:xxl font-size)
          :fontWeight 500
          :lineHeight (:xl line-height)}
     :h2 {:fontSize (:xl font-size)
          :fontWeight 500
          :lineHeight (:lg line-height)}
     :h3 {:fontSize (:lg font-size)
          :fontWeight 600
          :lineHeight (:md line-height)}
     :h4 {:fontSize (:md font-size)
          :fontWeight 600
          :lineHeight (:md line-height)}
     :h5 {:fontSize (:sm font-size)
          :fontWeight 600
          :lineHeight (:sm line-height)}
     :button {:fontSize "inherit"}
     :caption {:fontSize (:sm font-size)
               :lineHeight (:sm line-height)}
     :sm (:sm font-size)
     :md (:md font-size)
     :lg (:lg font-size)
     :xl (:xl font-size)
     :xxl (:xxl font-size)}))

(defn non-empty [s]
  (when (not= "" s)
    s))

(defn safe-get
  "Assert the requested property is found in the map.
  Defining colors without value could cause errors
  if the color is passed to some functions."
  [m ks]
  (or (get-in m ks)
      (throw (js/Error. (str "Missing property " (pr-str ks))))))

(defn parse-font-families [s]
  (when s
    (-> (str/replace s #"['\"]" "")
        (str/split #",")
        (->> (map str/trim)))))

(defn trim-font-family
  "Some font-families list incorrectly \"A, B, C\", which
  makes browser to look for font-family with name A, B, C
  instead of three different fonts.
  But keep \"A\", \"B\", \"C\" as-is."
  [s]
  (if (and (= \" (first s))
           (= \" (last s)))
    (let [trimmed (subs s 1 (dec (count s)))]
      (if (str/includes? trimmed "\"")
        s
        trimmed))
    s))

(defn css->json
  "Just handles properties used by create-ods-theme"
  [theme]
  {:color {:grey {:100 (:wpp-grey-color-100 theme)
                  :200 (:wpp-grey-color-200 theme)
                  :300 (:wpp-grey-color-300 theme)
                  :400 (:wpp-grey-color-400 theme)
                  :500 (:wpp-grey-color-500 theme)
                  :600 (:wpp-grey-color-600 theme)
                  :700 (:wpp-grey-color-700 theme)
                  :800 (:wpp-grey-color-800 theme)
                  :900 (:wpp-grey-color-900 theme)
                  :1000 (:wpp-grey-color-1000 theme)}
           :brand {:hover ""
                   :active ""
                   :default ""
                   :disabled ""}
           :white "#ffffff"
           :danger {:200 (:wpp-danger-color-200 theme)
                    :300 (:wpp-danger-color-300 theme)
                    :400 (:wpp-danger-color-400 theme)
                    :500 (:wpp-danger-color-500 theme)
                    :600 (:wpp-danger-color-600 theme)}
           :dataviz {:seq {:positive {:400 (:wpp-dataviz-color-seq-positive-400 theme)}
                           :warning {:400 (:wpp-dataviz-color-seq-warning-400 theme)}
                           :negative {:400 (:wpp-dataviz-color-seq-negative-400 theme)}
                           :grey {:300 (:wpp-dataviz-color-seq-grey-300 theme)}}}
           :primary {:100 (:wpp-primary-color-100 theme)
                     :200 (:wpp-primary-color-200 theme)
                     :300 (:wpp-primary-color-300 theme)
                     :400 (:wpp-primary-color-400 theme)
                     :500 (:wpp-primary-color-500 theme)
                     :600 (:wpp-primary-color-600 theme)
                     :700 (:wpp-primary-color-700 theme)
                     :800 (:wpp-primary-color-800 theme)}
           :success {:200 (:wpp-success-color-200 theme)
                     :400 (:wpp-success-color-400 theme)
                     :500 (:wpp-success-color-500 theme)}
           :warning {:200 (:wpp-warning-color-200 theme)
                     :400 (:wpp-warning-color-400 theme)
                     :500 (:wpp-warning-color-500 theme)}
           :highlight {:200 (:wpp-highlight-color-200 theme)
                       :400 (:wpp-highlight-color-400 theme)}}
   :font {:family (:wpp-font-family theme)}
   :typography {:5xl {:display {:fontFamily (:wpp-typography-5xl-display-font-family theme)}}
                :4xl {:display {:fontFamily (:wpp-typography-4xl-display-font-family theme)}}
                :3xl {:display {:fontFamily (:wpp-typography-3xl-display-font-family theme)}}}
   :border {:radius {:m (:wpp-border-radius-s theme)
                     :xs (:wpp-border-radius-xs theme)}}
   :boxShadow {:xs (:wpp-box-shadow-xs theme)
               :s (:wpp-box-shadow-s theme)
               :m (:wpp-box-shadow-m theme)
               :l (:wpp-box-shadow-l theme)
               :brand (:wpp-box-shadow-brand theme)}
   :overlay {:bgColor (:wpp-overlay-bg-color theme)}})

(def wpp-theme-vars (css->json (emit-theme-vars "wpp")))

(defn create-ods-theme [vars]
  [vars
   {:palette
    {:mode "light"
     ;; Reverse grey palette so they match the previous use
     :grey {:100 (safe-get vars [:color :grey :1000])
            :90 (safe-get vars [:color :grey :100])
            :80 (safe-get vars [:color :grey :200])
            :70 (safe-get vars [:color :grey :300])
            :60 (safe-get vars [:color :grey :400])
            :50 (safe-get vars [:color :grey :500])
            :40 (safe-get vars [:color :grey :600])
            :30 (safe-get vars [:color :grey :700])
            :20 (safe-get vars [:color :grey :800])
            :10 (safe-get vars [:color :grey :900])}
     :primary {:light (safe-get vars [:color :primary :300])
               :main (safe-get vars [:color :primary :500])
               :dark (safe-get vars [:color :primary :700])
               :selected (safe-get vars [:color :primary :100])
               :100 (safe-get vars [:color :primary :100])
               :200 (safe-get vars [:color :primary :200])
               :300 (safe-get vars [:color :primary :300])
               :400 (safe-get vars [:color :primary :400])
               :500 (safe-get vars [:color :primary :500])
               :600 (safe-get vars [:color :primary :600])
               :700 (safe-get vars [:color :primary :700])
               :800 (safe-get vars [:color :primary :800])}
     :default {:light (safe-get vars [:color :grey :100])
               :main (safe-get vars [:color :grey :200])
               :dark (safe-get vars [:color :grey :300])}
     :error {:background (safe-get vars [:color :danger :200])
             :light (safe-get vars [:color :danger :300])
             :main (safe-get vars [:color :danger :500])
             :dark (safe-get vars [:color :danger :600])}
     :warning (let [;; Some OS configs don't have this value so fallback
                    value (get-in vars [:color :warning :200])
                    bg (if-not (str/blank? value)
                         value
                         (safe-get wpp-theme-vars [:color :warning :200]))]
                {:background bg
                 :light bg
                 :main (safe-get vars [:color :warning :400])
                 :dark (safe-get vars [:color :warning :500])})
     :success {:background (safe-get vars [:color :success :200])
               :light (safe-get vars [:color :success :200])
               :main (safe-get vars [:color :success :400])
               :dark (safe-get vars [:color :success :500])}
     :text {:primary (safe-get vars [:color :grey :1000])
            :secondary (safe-get vars [:color :grey :700])
            :disabled (safe-get vars [:color :grey :500])
            :selected (safe-get vars [:color :primary :500])
            :subtle "rgba(8, 32, 64, 0.76)"}
     :data {:positive (safe-get vars [:color :dataviz :seq :positive :400])
            :warning (safe-get vars [:color :dataviz :seq :warning :400])
            :negative (safe-get vars [:color :dataviz :seq :negative :400])
            :grey {:300 (safe-get vars [:color :dataviz :seq :grey :300])}}
     :highlight {:contrastText (mui-styles/alpha (mui-styles/darken (safe-get vars [:color :highlight :200]) 0.60) 0.8)
                 :light (safe-get vars [:color :highlight :400])
                 :main (safe-get vars [:color :highlight :200])
                 :dark (safe-get vars [:color :highlight :200])}
     :overlay {:background (safe-get vars [:overlay :bgColor])}}
    :shape {:borderRadius (js/parseInt (safe-get vars [:border :radius :m]))}
    :shadows (into ["none"
                    (safe-get vars [:boxShadow :s])
                    (safe-get vars [:boxShadow :m])
                    (safe-get vars [:boxShadow :l])]
                   (repeat 21 "none"))
    :typography (deep-merge typography
                            {:fontFamily (let [default-font-family "-apple-system,BlinkMacSystemFont,\"Segoe UI\",Roboto,\"Helvetica Neue\",Arial,sans-serif,\"Apple Color Emoji\",\"Segoe UI Emoji\",\"Segoe UI Symbol\""
                                               font-family (trim-font-family (safe-get vars [:font :family]))]
                                           (if font-family
                                             ;; Add a default font as one theme uses font that isn't available now.
                                             (str font-family "," default-font-family)
                                             default-font-family))
                             ;; Essence-mediacom theme uses different font now for the three largest headers.
                             ;; They also list the header font on wpp-font-family variable, so it
                             ;; is automatically loaded. These are optional.
                             :h1 {:fontFamily (or (non-empty (get-in vars [:typography :5xl :heading :fontFamily]))
                                                  (non-empty (get-in vars [:typography :5xl :display :fontFamily])))}
                             :h2 {:fontFamily (non-empty (get-in vars [:typography :4xl :display :fontFamily]))}
                             :h3 {:fontFamily (non-empty (get-in vars [:typography :3xl :display :fontFamily]))}})
    :breakpoints breakpoints
    :components components}])

(defn get-table-border-color [^js theme]
  (if (= "light" (.. theme -palette -mode))
    (mui-styles/lighten (mui-styles/alpha (.. theme -palette -divider) 1) 0.88)
    (mui-styles/darken (mui-styles/alpha (.. theme -palette -divider) 1) 0.68)))

;; Initialize other themes when they are selected

(def wpp-theme (create-ods-theme wpp-theme-vars))

(defn get-selected-theme []
  (case (js/localStorage.getItem "theme")
    "akqa" (create-ods-theme (css->json (emit-theme-vars "akqa")))
    "bcw" (create-ods-theme (css->json (emit-theme-vars "bcw")))
    "choreograph" (create-ods-theme (css->json (emit-theme-vars "choreograph")))
    "essence-mediacom" (create-ods-theme (css->json (emit-theme-vars "essence-mediacom")))
    "groupm" (create-ods-theme (css->json (emit-theme-vars "groupm")))
    "landor-and-fitch" (create-ods-theme (css->json (emit-theme-vars "landor-and-fitch")))
    "mindshare" (create-ods-theme (css->json (emit-theme-vars "mindshare")))
    "msix" (create-ods-theme (css->json (emit-theme-vars "msix")))
    "ogilvy" (create-ods-theme (css->json (emit-theme-vars "ogilvy")))
    "openx" (create-ods-theme (css->json (emit-theme-vars "openx")))
    "vmlyr" (create-ods-theme (css->json (emit-theme-vars "vmly&r")))
    "wavemaker" (create-ods-theme (css->json (emit-theme-vars "wavemaker")))
    "wunderman-thompson" (create-ods-theme (css->json (emit-theme-vars "wunderman-thompson")))
    wpp-theme))

(defn select-theme! [theme]
  (js/localStorage.setItem "theme" theme)
  (.dispatchEvent js/window (js/Event. "themeChange")))

;; NOTE: This font loading solution does have some extra time before
;; the font is loaded so there will be some extra renders before the font
;; is loaded. Investigate faster solution once we know how the theme
;; is chosen.
;; The default theme fonts are loaded in index.html.
(defui font-family [{:keys [font-family-name]}]
  (let [font-href (get fonts font-family-name)]
    (uix/use-effect (fn []
                      (when font-href
                        (let [el (js/document.createElement "link")]
                          (set! (.-rel el) "stylesheet")
                          (set! (.-type el) "text/css")
                          (set! (.-href el) font-href)
                          (.appendChild js/document.body el)
                          (fn []
                            (.removeChild js/document.body el)))))
                    [font-href])
    nil))

(defui theme-provider [{:keys [children]}]
  (let [[_ force-update] (uix/use-state 0)
        os-enabled (use-subscribe [::os/enabled])
        os-theme (use-subscribe [::os/theme])
        [vars mui-theme-cljs] (if (and os-enabled os-theme)
                                (create-ods-theme os-theme)
                                (get-selected-theme))
        font-families (parse-font-families (:family (:font vars)))]
    (useEvent "themeChange" (fn [] (force-update inc)))
    ($ :<>
       (for [font-family-name font-families]
         ($ font-family {:key font-family-name :font-family-name font-family-name}))
       ($ mui-styles/ThemeProvider {:theme (-> mui-theme-cljs
                                               (clj->js)
                                               (mui-styles/createTheme))}
          ($ CssBaseline)
          children))))
